// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the lesson player (Education tier plan F7): a panel at the top
 * start corner that walks a teacher through a lesson canvas one step at a
 * time. Each step loads its dataset (a stream outside the plan shows its
 * name with a lock; a library dataset the app cannot play yet says so),
 * shows the students' questions and tasks and, folded, the teacher's notes.
 *
 * A lesson never moves the globe by itself (owner, 2026-10-05): the camera,
 * the date and the pins are the teacher's. "Keep this view for this step"
 * stores where the teacher left the camera and the date in this browser
 * (`lessonService.setStepSetting`), and coming back to the step puts them
 * back; "Download my lesson" saves the teacher's own Khipu in a `.zip`.
 * The panel is the teacher's alone: nothing in it goes to a class.
 *
 * AYNI (2026-10-09): a step can offer real-time variables to add over its
 * dataset, numbered in the order the lesson reveals them (cloud, then rain,
 * then lightning over the sea's temperature). Each press stacks one live
 * stream over the dataset, on the same date, and shows what to look for now
 * that it is on; a class follows. Never put on by the lesson itself, and a
 * new step takes off the ones the lesson added (the teacher's own picks in
 * Tools → Layers stay).
 *
 * AYNI (owner, 2026-10-05): it opens at the top start corner and can be
 * dragged anywhere by its title bar (or moved with the arrow keys from
 * its ⠿ grip), always kept whole on screen; a new lesson opens in the
 * corner again.
 */
import { getLocale, t } from '../i18n'
import type { Khipu, KhipuView, StepSetting } from '../services/khipu'
import { localized } from '../services/khipu'
import { buildLessonZip, lessonCopy, lessonExtras, ownLesson, promptsFor, resolveDatasetKey, resolveStepDataset, setStepSetting, stepOverlays, stepRealtime, type StepDataset, type StepOverlay, type StepRealtime } from '../services/lessonService'
import { saveKhipu } from '../services/khipuStore'
import { formatDate } from '../i18n/format'
import { ensureLibraryDataset } from '../services/lessonLibrary'
import { escapeAttr, escapeHtml } from './domUtils'
import { printWorksheet } from './lessonWorksheet'
import { clampPanel, isPanelShown, makeFloating } from './floatingPanel'
import type { VrKhipuState } from '../services/vrKhipuPanel'

export { clampPanel }

export interface LessonPlayerHost {
  /** Load a dataset the way the browse panel does; true once it is on the globe. */
  loadDataset(id: string): Promise<boolean>
  /** The dataset on the globe now. */
  currentDatasetId(): string | null
  /** The package a stream needs that the plan lacks, by name, or null. */
  streamLock(id: string): Promise<string | null>
  getView(): KhipuView | null
  setView(view: KhipuView): void
  /** The date on the globe (ISO), when the dataset has a time axis. */
  currentDate(): string | null
  seekToDate(iso: string): void
  openPlans(trigger: HTMLElement | null): void
  openDataTools(): void
  announce(message: string): void
  /** AYNI: whether the plan keeps Khipus on the account ("Save to my account"). */
  canKeep?(): boolean
  /** AYNI: the overlays on the globe now (two at most, `MAX_OVERLAYS`), by layer id. */
  overlayIds?(): readonly string[]
  /** AYNI: put this overlay on the globe, or take it off if it is on, as in Tools → Layers; false when the layer is not there. */
  toggleOverlay?(layerId: string, tint?: StepOverlay['tint']): Promise<boolean>
  /** AYNI: these overlays and no others (a Khipu step's; empty: none); false when a layer is not there. */
  setOverlays?(overlays: ReadonlyArray<{ layerId: string; tint?: StepOverlay['tint'] }>): Promise<boolean>
  /** AYNI: the real-time streams playing over the dataset now, the first lowest, by dataset id. */
  rtOverlayIds?(): readonly string[]
  /** AYNI: how many real-time streams this device plays at once. */
  rtCap?(): number
  /** AYNI: play these real-time streams over the dataset, in this order, and no others; resolves with what plays. */
  setRtOverlays?(ids: readonly string[]): Promise<readonly string[]>
  /**
   * AYNI: when the date on the globe is outside the dates the dataset and these
   * streams share, go to the latest such date; the date gone to (ISO), 'none'
   * when they share no date, null when the date could stay.
   */
  showRtDates?(ids: readonly string[]): Promise<string | 'none' | null>
  /** AYNI: remove every pin on the globe (a lesson starts and ends without pins; between its steps they stay). */
  clearPins?(): void
}

type DatasetLine =
  | { state: 'idle' }
  | { state: 'loading'; title: string }
  | { state: 'shown'; title: string; values: boolean }
  | { state: 'locked'; title: string; pkg: string }
  | { state: 'unavailable'; title: string }
  | { state: 'failed'; title: string; id: string }
  | { state: 'none' }

const PROMPT_LABEL: Record<string, string> = {
  question: 'lessons.player.prompt.question',
  task: 'lessons.player.prompt.task',
  caption: 'lessons.player.prompt.caption',
  note: 'lessons.player.prompt.note',
}

export interface LessonPlayerHandle {
  close(): void
  isOpen(): boolean
  khipuId(): string | null
  step(): number
  /** AYNI (Khipu plan, slice 4): the headset's buttons. Next on the last step finishes. */
  next(): void
  prev(): void
  /** What the headset's Khipu panel shows; the same object until something changed. */
  vrState(): VrKhipuState
  /** AYNI: add (or take off) the step's real-time variable number `i`, from 0 (the headset's buttons). */
  toggleRealtime(i: number): void
  /** AYNI: the real-time streams changed elsewhere (Tools → Layers, a room): redraw if it shows. */
  refresh(): void
}

let active: LessonPlayerHandle | null = null

/** The open lesson, if any (a second Start replaces it). */
export function activeLessonPlayer(): LessonPlayerHandle | null {
  return active
}

export function openLessonPlayer(k: Khipu, host: LessonPlayerHost, startAt = 0): LessonPlayerHandle {
  active?.close()
  // AYNI (owner, 2026-10-09): a lesson starts on a globe without pins, and leaves none behind.
  host.clearPins?.()
  const lang = getLocale()
  const steps = k.path.steps
  let index = Math.min(Math.max(0, startAt), steps.length - 1)
  let line: DatasetLine = { state: 'idle' }
  let folded = false
  let saveStatus: { text: string; ok: boolean } | null = null
  /** The extra on the globe instead of the step's own dataset, by key. */
  let extraShown: string | null = null
  const extras = lessonExtras(k)
  let generation = 0
  /** The real-time streams this lesson put on (not the teacher's own): a new step takes them off. */
  const rtAdded = new Set<string>()
  /** A step's variable that is outside the plan, once pressed: its package's name. */
  const rtLocked = new Map<string, string>()
  let rtBusy: string | null = null
  /** What a press on this step did to the date (moved to one the variables show on, or none they share); kept until the step changes. */
  let rtNote: string | null = null
  /** The streams playing when the panel was last drawn (`refresh` redraws when they change). */
  let rtDrawn = ''

  const panel = document.createElement('section')
  panel.id = 'lesson-player'
  panel.className = 'lesson-player'
  panel.setAttribute('aria-label', t('lessons.player.aria'))
  document.body.appendChild(panel)

  /** What the teacher kept on this step: in this browser, else in their own saved lesson. */
  const setting = (): StepSetting | undefined => {
    const step = steps[index]
    const local = lessonCopy(k.id)[step.id]
    if (local) return local
    if (!step.view && !step.time) return undefined
    return { ...(step.view ? { view: step.view } : {}), ...(step.time ? { time: step.time } : {}) }
  }

  function keptLabel(s: StepSetting | undefined): string {
    if (!s) return ''
    const what = s.view && s.time ? t('lessons.player.kept.both') : s.view ? t('lessons.player.kept.view') : t('lessons.player.kept.time')
    return t('lessons.player.kept', { what })
  }

  function datasetHtml(): string {
    switch (line.state) {
      case 'idle':
        return ''
      case 'none':
        return `<p class="lesson-dataset">${escapeHtml(t('lessons.player.dataset.none'))}</p>`
      case 'loading':
        return `<p class="lesson-dataset" aria-busy="true">${escapeHtml(t('lessons.player.dataset.loading', { title: line.title }))}</p>`
      case 'shown':
        return `<p class="lesson-dataset"><span class="lesson-dataset-name">${escapeHtml(line.title)}</span>${line.values ? ` <button type="button" class="lesson-link lesson-data">${escapeHtml(t('lessons.player.openData'))}</button>` : ''}</p>`
      case 'locked':
        return `<p class="lesson-dataset lesson-dataset-locked"><span aria-hidden="true">&#x1F512;&#xFE0E;</span> ${escapeHtml(t('lessons.player.dataset.locked', { title: line.title, package: line.pkg }))} <button type="button" class="lesson-link lesson-plans">${escapeHtml(t('lessons.plans'))}</button></p>`
      case 'unavailable':
        return `<p class="lesson-dataset lesson-dataset-missing">${escapeHtml(t('lessons.player.dataset.unavailable', { title: line.title }))}</p>`
      case 'failed':
        return `<p class="lesson-dataset lesson-dataset-missing">${escapeHtml(t('lessons.player.dataset.failed', { title: line.title }))} <button type="button" class="lesson-link lesson-again">${escapeHtml(t('lessons.player.dataset.again'))}</button></p>`
    }
  }

  /** The step's suggested overlays, as one-press buttons, once its dataset is up. */
  function overlaysHtml(): string {
    if (line.state !== 'shown' || extraShown || !host.toggleOverlay) return ''
    const suggested = stepOverlays(k, steps[index], lang)
    if (!suggested.length) return ''
    const on = host.overlayIds?.() ?? []
    return `<p class="lesson-overlays"><span class="lesson-overlays-label">${escapeHtml(t('lessons.player.overlays'))}</span> ${suggested.map((o) =>
      `<button type="button" class="lesson-chip lesson-overlay" data-layer="${escapeAttr(o.layerId)}"${o.tint ? ` data-tint="${escapeAttr(o.tint)}"` : ''} aria-pressed="${on.includes(o.layerId)}">${escapeHtml(o.title)}</button>`).join(' ')}</p>`
  }

  async function toggleOverlay(button: HTMLElement): Promise<void> {
    const id = button.dataset.layer ?? ''
    const on = button.getAttribute('aria-pressed') === 'true'
    const ok = await host.toggleOverlay?.(id, (button.dataset.tint as StepOverlay['tint']) || undefined)
    if (ok === false) host.announce(t('lessons.player.overlayMissing', { title: button.textContent ?? '' }))
    else host.announce(t(on ? 'lessons.player.overlayOff' : 'lessons.player.overlayOn', { title: button.textContent ?? '' }))
    render()
  }

  const rtPlaying = (): readonly string[] => host.rtOverlayIds?.() ?? []

  /** The step's real-time variables, as a numbered row, once its dataset is up; each shows what to look for when on. */
  function realtimeHtml(): string {
    if (line.state !== 'shown' || extraShown || !host.setRtOverlays) return ''
    const offered = stepRealtime(k, steps[index], lang)
    if (!offered.length) return ''
    const playing = rtPlaying()
    const next = offered.find((r) => !playing.includes(r.datasetId))
    const cap = host.rtCap?.() ?? offered.length
    const items = offered.map((r, i) => {
      const on = playing.includes(r.datasetId)
      const pkg = rtLocked.get(r.datasetId)
      return `<li class="lesson-rt-item">
          <button type="button" class="lesson-chip lesson-rt${r === next ? ' lesson-rt-next' : ''}" data-rt="${escapeAttr(r.datasetId)}" aria-pressed="${on}"${rtBusy === r.datasetId ? ' aria-busy="true"' : ''}><span class="lesson-rt-n" aria-hidden="true">${i + 1}</span> ${pkg ? '<span aria-hidden="true">&#x1F512;&#xFE0E;</span> ' : ''}${escapeHtml(r.title)}</button>
          ${on && r.prompt ? `<p class="lesson-rt-look"><span class="lesson-prompt-kind">${escapeHtml(t('lessons.player.rt.look'))}</span> ${escapeHtml(r.prompt)}</p>` : ''}
          ${pkg && !on ? `<p class="lesson-dataset-locked lesson-rt-locked">${escapeHtml(t('lessons.player.dataset.locked', { title: r.title, package: pkg }))} <button type="button" class="lesson-link lesson-plans">${escapeHtml(t('lessons.plans'))}</button></p>` : ''}
        </li>`
    }).join('')
    const ours = playing.some((id) => rtAdded.has(id))
    return `<div class="lesson-rt-block">
        <p class="lesson-overlays-label" title="${escapeAttr(t('tools.layers.rt.note'))}">${escapeHtml(t('lessons.player.rt.label'))}</p>
        <ol class="lesson-rt-list">${items}</ol>
        ${rtNote ? `<p class="lesson-rt-note" role="status">${escapeHtml(rtNote)}</p>` : ''}
        ${offered.length > cap ? `<p class="lesson-rt-cap">${escapeHtml(t('tools.layers.rt.capMany', { count: cap }))}</p>` : ''}
        ${ours ? `<button type="button" class="lesson-link lesson-rt-clear">${escapeHtml(t('lessons.player.rt.clear'))}</button>` : ''}
      </div>`
  }

  /** Add the stream on top of what plays (the first added makes way when the device is full), or take it off. */
  async function toggleRealtime(r: StepRealtime): Promise<void> {
    if (rtBusy || !host.setRtOverlays) return
    const playing = rtPlaying()
    const on = playing.includes(r.datasetId)
    if (!on) {
      const pkg = await host.streamLock(r.datasetId)
      if (pkg) {
        rtLocked.set(r.datasetId, pkg)
        render()
        host.announce(t('lessons.player.dataset.locked', { title: r.title, package: pkg }))
        return
      }
    }
    const cap = Math.max(1, host.rtCap?.() ?? STEP_RT_FALLBACK_CAP)
    const want = on ? playing.filter((id) => id !== r.datasetId) : [...playing, r.datasetId].slice(-cap)
    rtBusy = r.datasetId
    render()
    const gen = generation
    let now: readonly string[] = playing
    let moved: string | 'none' | null = null
    try {
      now = await host.setRtOverlays(want)
      // A stream is hidden outside its own dates: put the dataset on one they all show on.
      if (!on && now.includes(r.datasetId)) moved = (await host.showRtDates?.(now)) ?? null
    } finally {
      rtBusy = null
    }
    if (gen !== generation || !isPanelShown(panel)) return
    if (moved === 'none') rtNote = t('lessons.player.rt.noDates', { title: r.title })
    else if (moved) rtNote = t('lessons.player.rt.moved', { date: formatDate(new Date(moved), { dateStyle: 'medium', timeZone: 'UTC' }) })
    if (on) rtAdded.delete(r.datasetId)
    else if (now.includes(r.datasetId)) rtAdded.add(r.datasetId)
    render()
    panel.querySelector<HTMLElement>(`.lesson-rt[data-rt="${CSS.escape(r.datasetId)}"]`)?.focus()
    if (on) host.announce(t('lessons.player.rt.off', { title: r.title }))
    else if (!now.includes(r.datasetId)) host.announce(t('lessons.player.rt.failed', { title: r.title }))
    else host.announce([t('lessons.player.rt.on', { title: r.title }), rtNote ?? '', r.prompt ? `${t('lessons.player.rt.look')}: ${r.prompt}` : ''].filter(Boolean).join(' '))
  }

  /** Take off what this lesson put on (a new step, Close); the teacher's own streams stay. */
  async function takeOffOurs(): Promise<void> {
    if (!rtAdded.size || !host.setRtOverlays) return
    const playing = rtPlaying()
    const keep = playing.filter((id) => !rtAdded.has(id))
    rtAdded.clear()
    if (keep.length !== playing.length) await host.setRtOverlays(keep)
  }

  /** Bumped by every redraw: the headset's panel redraws when it changes. */
  let revision = 0
  let vrCache: { revision: number; state: VrKhipuState } | null = null

  function datasetText(): string {
    switch (line.state) {
      case 'loading': return t('lessons.player.dataset.loading', { title: line.title })
      case 'shown': return line.title
      case 'locked': return t('lessons.player.dataset.locked', { title: line.title, package: line.pkg })
      case 'unavailable': return t('lessons.player.dataset.unavailable', { title: line.title })
      case 'failed': return t('lessons.player.dataset.failed', { title: line.title })
      case 'none': return t('lessons.player.dataset.none')
      default: return ''
    }
  }

  function vrState(): VrKhipuState {
    if (vrCache?.revision === revision) return vrCache.state
    const step = steps[index]
    const offered = line.state === 'shown' && !extraShown && host.setRtOverlays ? stepRealtime(k, step, lang) : []
    const playing = rtPlaying()
    // The headset shows few lines: what to look for on the variable added last comes first, the others after the prompts.
    const on = offered.filter((r) => r.prompt && playing.includes(r.datasetId)).sort((a, b) => playing.indexOf(a.datasetId) - playing.indexOf(b.datasetId))
    const look = (r: StepRealtime) => `${t('lessons.player.rt.look')}: ${r.prompt}`
    const latest = on.at(-1)
    const state: VrKhipuState = {
      title: localized(k.title, lang),
      progress: `${t('khipu.kind.lesson')} · ${t('lessons.player.step', { n: index + 1, total: steps.length })}`,
      heading: localized(step.title, lang),
      dataset: datasetText(),
      lines: [...(latest ? [look(latest)] : []), ...promptsFor(step, 'students').map((p) => `${t((PROMPT_LABEL[p.kind] ?? 'lessons.player.prompt.note') as Parameters<typeof t>[0])}: ${p.text}`), ...on.slice(0, -1).map(look)],
      media: 0,
      canPrev: index > 0,
      isLast: index === steps.length - 1,
      offersPlay: false,
      playing: false,
      ...(offered.length ? { variables: offered.map((r) => ({ title: r.title, on: playing.includes(r.datasetId), locked: rtLocked.has(r.datasetId) })) } : {}),
    }
    vrCache = { revision, state }
    return state
  }

  function render(): void {
    revision++
    rtDrawn = rtPlaying().join(' ')
    const step = steps[index]
    const forStudents = promptsFor(step, 'students')
    const forTeacher = promptsFor(step, 'presenter')
    const kept = setting()
    const last = index === steps.length - 1
    panel.classList.toggle('folded', folded)
    panel.innerHTML = `
      <div class="lesson-head">
        <button type="button" class="lesson-icon lesson-grip" aria-label="${escapeAttr(t('lessons.player.move'))}" title="${escapeAttr(t('lessons.player.move'))}">&#x283F;</button>
        <div class="lesson-head-text">
          <span class="lesson-title">${escapeHtml(localized(k.title, lang))}</span>
          <span class="lesson-progress">${escapeHtml(t('lessons.player.step', { n: index + 1, total: steps.length }))}</span>
        </div>
        <button type="button" class="lesson-icon lesson-fold" aria-expanded="${String(!folded)}" aria-label="${escapeAttr(t(folded ? 'lessons.player.expand' : 'lessons.player.collapse'))}">${folded ? '&#x25BE;' : '&#x25B4;'}</button>
        <button type="button" class="lesson-icon lesson-close" aria-label="${escapeAttr(t('lessons.player.close'))}">&#x2715;</button>
      </div>
      <div class="lesson-body"${folded ? ' hidden' : ''}>
        <h3 class="lesson-step-title">${escapeHtml(localized(step.title, lang))}</h3>
        ${datasetHtml()}
        ${overlaysHtml()}
        ${realtimeHtml()}
        ${forStudents.length ? `<h4 class="lesson-sub">${escapeHtml(t('lessons.player.students'))}</h4><ul class="lesson-prompts">${forStudents.map((p) => `<li><span class="lesson-prompt-kind">${escapeHtml(t((PROMPT_LABEL[p.kind] ?? 'lessons.player.prompt.note') as Parameters<typeof t>[0]))}</span> ${escapeHtml(p.text)}</li>`).join('')}</ul>` : ''}
        ${forTeacher.length ? `<details class="lesson-notes"><summary>${escapeHtml(t('lessons.player.teacherNotes'))}</summary><ul>${forTeacher.map((p) => `<li>${escapeHtml(p.text)}</li>`).join('')}</ul></details>` : ''}
        ${extras.length ? `<details class="lesson-extras"${extraShown ? ' open' : ''}><summary>${escapeHtml(t('lessons.player.extras'))}</summary><ul>${extras.map((x) => `<li><button type="button" class="lesson-link lesson-extra" data-key="${escapeAttr(x.key)}"${extraShown === x.key ? ' aria-current="true"' : ''}>${escapeHtml(x.title)}</button></li>`).join('')}${extraShown ? `<li><button type="button" class="lesson-link lesson-extra-back">${escapeHtml(t('lessons.player.extraBack'))}</button></li>` : ''}</ul></details>` : ''}
        <div class="lesson-keep">
          ${kept ? `<span class="lesson-kept">${escapeHtml(keptLabel(kept))}</span> <button type="button" class="lesson-link lesson-forget">${escapeHtml(t('lessons.player.forget'))}</button>` : ''}
          <button type="button" class="lesson-btn lesson-btn-quiet lesson-keep-btn">${escapeHtml(t(kept ? 'lessons.player.keepAgain' : 'lessons.player.keep'))}</button>
        </div>
        <div class="lesson-nav">
          <button type="button" class="lesson-btn lesson-btn-quiet lesson-prev"${index === 0 ? ' disabled' : ''}>${escapeHtml(t('lessons.player.prev'))}</button>
          <button type="button" class="lesson-btn lesson-next">${escapeHtml(t(last ? 'lessons.player.finish' : 'lessons.player.next'))}</button>
        </div>
        <div class="lesson-links">
          <button type="button" class="lesson-link lesson-download">${escapeHtml(t('lessons.player.download'))}</button>
          <button type="button" class="lesson-link lesson-print">${escapeHtml(t('lessons.worksheet.print'))}</button>
          ${host.canKeep?.() ? `<button type="button" class="lesson-link lesson-save">${escapeHtml(t('lessons.player.save'))}</button>` : ''}
        </div>
        ${saveStatus ? `<p class="lesson-save-status${saveStatus.ok ? '' : ' lesson-save-error'}" role="status">${escapeHtml(saveStatus.text)}</p>` : ''}
        <p class="lesson-notice">${escapeHtml(t('lessons.notice'))}</p>
      </div>`
  }

  /** Put back what the teacher kept on this step, once its dataset is on the globe. */
  function applyKept(): void {
    const kept = setting()
    if (kept?.view) host.setView(kept.view)
    if (kept?.time?.at) host.seekToDate(kept.time.at)
  }

  async function enter(i: number): Promise<void> {
    index = i
    saveStatus = null
    extraShown = null
    rtLocked.clear()
    rtNote = null
    const gen = ++generation
    await takeOffOurs()
    if (gen !== generation) return
    const step = steps[index]
    const resolved: StepDataset = await resolveStepDataset(k, step, lang)
    if (gen !== generation) return
    if (resolved.kind === 'none') {
      line = { state: 'none' }
      render()
      return
    }
    if (resolved.kind === 'unavailable') {
      line = { state: 'unavailable', title: resolved.title }
      render()
      return
    }
    await showDataset(resolved.datasetId, resolved.title, resolved.dataEnabled, gen)
  }

  /** Put one of the lesson's extras on the globe; the step's own dataset is a press away. */
  async function showExtra(key: string): Promise<void> {
    saveStatus = null
    rtNote = null
    const gen = ++generation
    // The step's variables go with its dataset.
    await takeOffOurs()
    if (gen !== generation) return
    const resolved = await resolveDatasetKey(k, key, lang)
    if (gen !== generation) return
    extraShown = key
    if (resolved.kind === 'none' || resolved.kind === 'unavailable') {
      line = { state: 'unavailable', title: resolved.kind === 'unavailable' ? resolved.title : key }
      render()
      return
    }
    await showDataset(resolved.datasetId, resolved.title, resolved.dataEnabled, gen, false)
  }

  async function showDataset(id: string, title: string, values: boolean, gen: number, keptView = true): Promise<void> {
    if (host.currentDatasetId() === id) {
      line = { state: 'shown', title, values }
      render()
      if (keptView) applyKept()
      return
    }
    line = { state: 'loading', title }
    render()
    if (id.startsWith('LIB_') && !(await ensureLibraryDataset(id))) {
      if (gen !== generation) return
      line = { state: 'unavailable', title }
      render()
      return
    }
    const pkg = await host.streamLock(id)
    if (gen !== generation) return
    if (pkg) {
      line = { state: 'locked', title, pkg }
      render()
      return
    }
    const ok = await host.loadDataset(id)
    if (gen !== generation) return
    line = ok ? { state: 'shown', title, values } : { state: 'failed', title, id }
    render()
    if (ok && keptView) {
      host.announce(t('lessons.player.announce', { n: index + 1, title: localized(steps[index].title, lang) }))
      applyKept()
    } else if (ok) host.announce(title)
  }

  function keep(): void {
    const view = host.getView()
    const at = host.currentDate()
    const next: StepSetting = {
      ...(view ? { view: roundView(view) } : {}),
      ...(at ? { time: { at } } : {}),
    }
    setStepSetting(k.id, steps[index].id, next)
    render()
    host.announce(keptLabel(next))
  }

  /** Keep the teacher's own lesson on their account (it replaces the one saved before). */
  async function save(trigger: HTMLElement): Promise<void> {
    trigger.setAttribute('aria-busy', 'true')
    saveStatus = { text: t('lessons.player.saving'), ok: true }
    render()
    const result = await saveKhipu(ownLesson(k, lessonCopy(k.id)))
    saveStatus = result.ok
      ? { text: t('lessons.player.saved', { date: formatDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) }), ok: true }
      : { text: t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed'), ok: false }
    render()
    host.announce(saveStatus.text)
  }

  async function download(trigger: HTMLElement): Promise<void> {
    trigger.setAttribute('aria-busy', 'true')
    try {
      const { blob, filename } = await buildLessonZip(k, lessonCopy(k.id))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      host.announce(t('lessons.player.downloaded', { filename }))
    } finally {
      trigger.removeAttribute('aria-busy')
    }
  }

  // Dragged by its title bar, or moved by the arrow keys from its ⠿ grip.
  const floating = makeFloating(panel, { headSelector: '.lesson-head', gripClass: 'lesson-grip' })
  const keepOnScreen = () => floating.keepOnScreen()

  panel.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const button = target.closest<HTMLElement>('button')
    if (!button) return
    if (button.classList.contains('lesson-close')) return handle.close()
    if (button.classList.contains('lesson-fold')) {
      folded = !folded
      render()
      keepOnScreen()
      panel.querySelector<HTMLElement>('.lesson-fold')?.focus()
      return
    }
    if (button.classList.contains('lesson-prev') && index > 0) return void enter(index - 1)
    if (button.classList.contains('lesson-next')) {
      if (index === steps.length - 1) return handle.close()
      return void enter(index + 1)
    }
    if (button.classList.contains('lesson-keep-btn')) return keep()
    if (button.classList.contains('lesson-forget')) {
      setStepSetting(k.id, steps[index].id, null)
      return render()
    }
    if (button.classList.contains('lesson-plans')) return host.openPlans(button)
    if (button.classList.contains('lesson-data')) return host.openDataTools()
    if (button.classList.contains('lesson-again') && line.state === 'failed') {
      const { id, title } = line
      return void showDataset(id, title, false, ++generation)
    }
    if (button.classList.contains('lesson-overlay')) return void toggleOverlay(button)
    if (button.classList.contains('lesson-rt') && button.dataset.rt) {
      const r = stepRealtime(k, steps[index], lang).find((x) => x.datasetId === button.dataset.rt)
      return void (r && toggleRealtime(r))
    }
    if (button.classList.contains('lesson-rt-clear')) {
      rtNote = null
      return void takeOffOurs().then(() => {
        render()
        host.announce(t('lessons.player.rt.cleared'))
      })
    }
    if (button.classList.contains('lesson-extra') && button.dataset.key) return void showExtra(button.dataset.key)
    if (button.classList.contains('lesson-extra-back')) return void enter(index)
    if (button.classList.contains('lesson-download')) return void download(button)
    if (button.classList.contains('lesson-print')) return printWorksheet(k, 'students')
    if (button.classList.contains('lesson-save')) return void save(button)
  })

  const handle: LessonPlayerHandle = {
    close: () => {
      generation++
      void takeOffOurs()
      host.clearPins?.()
      floating.leave()
      if (active === handle) active = null
    },
    isOpen: () => isPanelShown(panel),
    khipuId: () => k.id,
    step: () => index,
    next: () => {
      if (index === steps.length - 1) handle.close()
      else void enter(index + 1)
    },
    prev: () => {
      if (index > 0) void enter(index - 1)
    },
    vrState,
    toggleRealtime: (i) => {
      const r = line.state === 'shown' && !extraShown ? stepRealtime(k, steps[index], lang)[i] : undefined
      if (r) void toggleRealtime(r)
    },
    refresh: () => {
      if (isPanelShown(panel) && rtPlaying().join(' ') !== rtDrawn) render()
    },
  }
  active = handle
  render()
  void enter(index)
  return handle
}

/** How many real-time streams to allow when the host does not say (a computer's). */
const STEP_RT_FALLBACK_CAP = 4

/** A camera worth keeping: to about 100 m, and within the schema's bounds. */
export function roundView(v: KhipuView): KhipuView {
  const r = (x: number, d: number) => Math.round(x * 10 ** d) / 10 ** d
  const lon = ((((v.lon + 180) % 360) + 360) % 360) - 180
  return {
    lat: r(Math.max(-90, Math.min(90, v.lat)), 3),
    lon: r(lon, 3),
    zoom: r(Math.max(0, Math.min(10, v.zoom)), 2),
    ...(v.bearing !== undefined ? { bearing: r(v.bearing, 1) } : {}),
    ...(v.pitch !== undefined ? { pitch: r(Math.max(0, Math.min(85, v.pitch)), 1) } : {}),
  }
}
